// File: frontend/src/constants/emoji.constants.ts

/*
 * Emoji data for the message composer's emoji popup.
 *
 * Source: the official Unicode emoji list (the same data behind unicode.org's
 * emoji-test.txt), in its standard group order. It was taken from the
 * "unicode-emoji-json" npm package at build time and pasted in here, so there
 * is no runtime dependency.
 *
 * Filters applied when the list was generated:
 *  - Emoji version <= 12.1 only. Windows 10's emoji font stops at 12.x, so newer
 *    emoji would show up as empty boxes for a large share of desktop readers.
 *  - Country flags are left out. Windows has no flag glyphs at all and renders
 *    them as two plain letters ("TR", "GB"); only the non-country flags remain.
 *  - Skin-tone variants are left out; only the default (yellow) form is listed.
 *
 * Anything not listed here can still be typed from the device's own emoji
 * keyboard — the backend and the utf8mb4 columns accept every emoji.
 */

export interface EmojiGroup {
  /** Stable id, also used as the active-tab state value */
  id: string;
  /** Emoji shown on the group's tab */
  icon: string;
  /** i18n key used as the tab tooltip */
  labelKey: string;
  emojis: string[];
}

export const EMOJI_GROUPS: EmojiGroup[] = [
  {
    id: "smileys",
    icon: "😀",
    labelKey: "messages_emoji_group_smileys",
    emojis: [
      "😀", "😃", "😄", "😁", "😆", "😅", "🤣", "😂", "🙂", "🙃",
      "😉", "😊", "😇", "🥰", "😍", "🤩", "😘", "😗", "☺️", "😚",
      "😙", "😋", "😛", "😜", "🤪", "😝", "🤑", "🤗", "🤭", "🤫",
      "🤔", "🤐", "🤨", "😐", "😑", "😶", "😏", "😒", "🙄", "😬",
      "🤥", "😌", "😔", "😪", "🤤", "😴", "😷", "🤒", "🤕", "🤢",
      "🤮", "🤧", "🥵", "🥶", "🥴", "😵", "🤯", "🤠", "🥳", "😎",
      "🤓", "🧐", "😕", "😟", "🙁", "☹️", "😮", "😯", "😲", "😳",
      "🥺", "😦", "😧", "😨", "😰", "😥", "😢", "😭", "😱", "😖",
      "😣", "😞", "😓", "😩", "😫", "🥱", "😤", "😡", "😠", "🤬",
      "😈", "👿", "💀", "☠️", "💩", "🤡", "👹", "👺", "👻", "👽",
      "👾", "🤖", "😺", "😸", "😹", "😻", "😼", "😽", "🙀", "😿",
      "😾", "🙈", "🙉", "🙊", "💌", "💘", "💝", "💖", "💗", "💓",
      "💞", "💕", "💟", "❣️", "💔", "❤️", "🧡", "💛", "💚", "💙",
      "💜", "🤎", "🖤", "🤍", "💋", "💯", "💢", "💥", "💫", "💦",
      "💨", "🕳️", "💬", "👁️‍🗨️", "🗨️", "🗯️", "💭", "💤",
    ],
  },
  {
    id: "people",
    icon: "👋",
    labelKey: "messages_emoji_group_people",
    emojis: [
      "👋", "🤚", "🖐️", "✋", "🖖", "👌", "🤏", "✌️", "🤞", "🤟",
      "🤘", "🤙", "👈", "👉", "👆", "🖕", "👇", "☝️", "👍", "👎",
      "✊", "👊", "🤛", "🤜", "👏", "🙌", "👐", "🤲", "🤝", "🙏",
      "✍️", "💅", "🤳", "💪", "🦾", "🦿", "🦵", "🦶", "👂", "🦻",
      "👃", "🧠", "🦷", "🦴", "👀", "👁️", "👅", "👄", "👶", "🧒",
      "👦", "👧", "🧑", "👱", "👨", "🧔", "👨‍🦰", "👨‍🦱", "👨‍🦳", "👨‍🦲",
      "👩", "👩‍🦰", "🧑‍🦰", "👩‍🦱", "🧑‍🦱", "👩‍🦳", "🧑‍🦳", "👩‍🦲", "🧑‍🦲", "👱‍♀️",
      "👱‍♂️", "🧓", "👴", "👵", "🙍", "🙍‍♂️", "🙍‍♀️", "🙎", "🙎‍♂️", "🙎‍♀️",
      "🙅", "🙅‍♂️", "🙅‍♀️", "🙆", "🙆‍♂️", "🙆‍♀️", "💁", "💁‍♂️", "💁‍♀️", "🙋",
      "🙋‍♂️", "🙋‍♀️", "🧏", "🧏‍♂️", "🧏‍♀️", "🙇", "🙇‍♂️", "🙇‍♀️", "🤦", "🤦‍♂️",
      "🤦‍♀️", "🤷", "🤷‍♂️", "🤷‍♀️", "🧑‍⚕️", "👨‍⚕️", "👩‍⚕️", "🧑‍🎓", "👨‍🎓", "👩‍🎓",
      "🧑‍🏫", "👨‍🏫", "👩‍🏫", "🧑‍⚖️", "👨‍⚖️", "👩‍⚖️", "🧑‍🌾", "👨‍🌾", "👩‍🌾", "🧑‍🍳",
      "👨‍🍳", "👩‍🍳", "🧑‍🔧", "👨‍🔧", "👩‍🔧", "🧑‍🏭", "👨‍🏭", "👩‍🏭", "🧑‍💼", "👨‍💼",
      "👩‍💼", "🧑‍🔬", "👨‍🔬", "👩‍🔬", "🧑‍💻", "👨‍💻", "👩‍💻", "🧑‍🎤", "👨‍🎤", "👩‍🎤",
      "🧑‍🎨", "👨‍🎨", "👩‍🎨", "🧑‍✈️", "👨‍✈️", "👩‍✈️", "🧑‍🚀", "👨‍🚀", "👩‍🚀", "🧑‍🚒",
      "👨‍🚒", "👩‍🚒", "👮", "👮‍♂️", "👮‍♀️", "🕵️", "🕵️‍♂️", "🕵️‍♀️", "💂", "💂‍♂️",
      "💂‍♀️", "👷", "👷‍♂️", "👷‍♀️", "🤴", "👸", "👳", "👳‍♂️", "👳‍♀️", "👲",
      "🧕", "🤵", "👰", "🤰", "🤱", "👼", "🎅", "🤶", "🦸", "🦸‍♂️",
      "🦸‍♀️", "🦹", "🦹‍♂️", "🦹‍♀️", "🧙", "🧙‍♂️", "🧙‍♀️", "🧚", "🧚‍♂️", "🧚‍♀️",
      "🧛", "🧛‍♂️", "🧛‍♀️", "🧜", "🧜‍♂️", "🧜‍♀️", "🧝", "🧝‍♂️", "🧝‍♀️", "🧞",
      "🧞‍♂️", "🧞‍♀️", "🧟", "🧟‍♂️", "🧟‍♀️", "💆", "💆‍♂️", "💆‍♀️", "💇", "💇‍♂️",
      "💇‍♀️", "🚶", "🚶‍♂️", "🚶‍♀️", "🧍", "🧍‍♂️", "🧍‍♀️", "🧎", "🧎‍♂️", "🧎‍♀️",
      "🧑‍🦯", "👨‍🦯", "👩‍🦯", "🧑‍🦼", "👨‍🦼", "👩‍🦼", "🧑‍🦽", "👨‍🦽", "👩‍🦽", "🏃",
      "🏃‍♂️", "🏃‍♀️", "💃", "🕺", "🕴️", "👯", "👯‍♂️", "👯‍♀️", "🧖", "🧖‍♂️",
      "🧖‍♀️", "🧗", "🧗‍♂️", "🧗‍♀️", "🤺", "🏇", "⛷️", "🏂", "🏌️", "🏌️‍♂️",
      "🏌️‍♀️", "🏄", "🏄‍♂️", "🏄‍♀️", "🚣", "🚣‍♂️", "🚣‍♀️", "🏊", "🏊‍♂️", "🏊‍♀️",
      "⛹️", "⛹️‍♂️", "⛹️‍♀️", "🏋️", "🏋️‍♂️", "🏋️‍♀️", "🚴", "🚴‍♂️", "🚴‍♀️", "🚵",
      "🚵‍♂️", "🚵‍♀️", "🤸", "🤸‍♂️", "🤸‍♀️", "🤼", "🤼‍♂️", "🤼‍♀️", "🤽", "🤽‍♂️",
      "🤽‍♀️", "🤾", "🤾‍♂️", "🤾‍♀️", "🤹", "🤹‍♂️", "🤹‍♀️", "🧘", "🧘‍♂️", "🧘‍♀️",
      "🛀", "🛌", "🧑‍🤝‍🧑", "👭", "👫", "👬", "💏", "👩‍❤️‍💋‍👨", "👨‍❤️‍💋‍👨", "👩‍❤️‍💋‍👩",
      "💑", "👩‍❤️‍👨", "👨‍❤️‍👨", "👩‍❤️‍👩", "👨‍👩‍👦", "👨‍👩‍👧", "👨‍👩‍👧‍👦", "👨‍👩‍👦‍👦", "👨‍👩‍👧‍👧", "👨‍👨‍👦",
      "👨‍👨‍👧", "👨‍👨‍👧‍👦", "👨‍👨‍👦‍👦", "👨‍👨‍👧‍👧", "👩‍👩‍👦", "👩‍👩‍👧", "👩‍👩‍👧‍👦", "👩‍👩‍👦‍👦", "👩‍👩‍👧‍👧", "👨‍👦",
      "👨‍👦‍👦", "👨‍👧", "👨‍👧‍👦", "👨‍👧‍👧", "👩‍👦", "👩‍👦‍👦", "👩‍👧", "👩‍👧‍👦", "👩‍👧‍👧", "🗣️",
      "👤", "👥", "👪", "👣",
    ],
  },
  {
    id: "animals",
    icon: "🐶",
    labelKey: "messages_emoji_group_animals",
    emojis: [
      "🐵", "🐒", "🦍", "🦧", "🐶", "🐕", "🦮", "🐕‍🦺", "🐩", "🐺",
      "🦊", "🦝", "🐱", "🐈", "🦁", "🐯", "🐅", "🐆", "🐴", "🐎",
      "🦄", "🦓", "🦌", "🐮", "🐂", "🐃", "🐄", "🐷", "🐖", "🐗",
      "🐽", "🐏", "🐑", "🐐", "🐪", "🐫", "🦙", "🦒", "🐘", "🦏",
      "🦛", "🐭", "🐁", "🐀", "🐹", "🐰", "🐇", "🐿️", "🦔", "🦇",
      "🐻", "🐨", "🐼", "🦥", "🦦", "🦨", "🦘", "🦡", "🐾", "🦃",
      "🐔", "🐓", "🐣", "🐤", "🐥", "🐦", "🐧", "🕊️", "🦅", "🦆",
      "🦢", "🦉", "🦩", "🦚", "🦜", "🐸", "🐊", "🐢", "🦎", "🐍",
      "🐲", "🐉", "🦕", "🦖", "🐳", "🐋", "🐬", "🐟", "🐠", "🐡",
      "🦈", "🐙", "🐚", "🦀", "🦞", "🦐", "🦑", "🦪", "🐌", "🦋",
      "🐛", "🐜", "🐝", "🐞", "🦗", "🕷️", "🕸️", "🦂", "🦟", "🦠",
      "💐", "🌸", "💮", "🏵️", "🌹", "🥀", "🌺", "🌻", "🌼", "🌷",
      "🌱", "🌲", "🌳", "🌴", "🌵", "🌾", "🌿", "☘️", "🍀", "🍁",
      "🍂", "🍃", "🍄",
    ],
  },
  {
    id: "food",
    icon: "🍔",
    labelKey: "messages_emoji_group_food",
    emojis: [
      "🍇", "🍈", "🍉", "🍊", "🍋", "🍌", "🍍", "🥭", "🍎", "🍏",
      "🍐", "🍑", "🍒", "🍓", "🥝", "🍅", "🥥", "🥑", "🍆", "🥔",
      "🥕", "🌽", "🌶️", "🥒", "🥬", "🥦", "🧄", "🧅", "🥜", "🌰",
      "🍞", "🥐", "🥖", "🥨", "🥯", "🥞", "🧇", "🧀", "🍖", "🍗",
      "🥩", "🥓", "🍔", "🍟", "🍕", "🌭", "🥪", "🌮", "🌯", "🥙",
      "🧆", "🥚", "🍳", "🥘", "🍲", "🥣", "🥗", "🍿", "🧈", "🧂",
      "🥫", "🍱", "🍘", "🍙", "🍚", "🍛", "🍜", "🍝", "🍠", "🍢",
      "🍣", "🍤", "🍥", "🥮", "🍡", "🥟", "🥠", "🥡", "🍦", "🍧",
      "🍨", "🍩", "🍪", "🎂", "🍰", "🧁", "🥧", "🍫", "🍬", "🍭",
      "🍮", "🍯", "🍼", "🥛", "☕", "🍵", "🍶", "🍾", "🍷", "🍸",
      "🍹", "🍺", "🍻", "🥂", "🥃", "🥤", "🧃", "🧉", "🧊", "🥢",
      "🍽️", "🍴", "🥄", "🔪", "🏺",
    ],
  },
  {
    id: "travel",
    icon: "🌍",
    labelKey: "messages_emoji_group_travel",
    emojis: [
      "🌍", "🌎", "🌏", "🌐", "🗺️", "🗾", "🧭", "🏔️", "⛰️", "🌋",
      "🗻", "🏕️", "🏖️", "🏜️", "🏝️", "🏞️", "🏟️", "🏛️", "🏗️", "🧱",
      "🏘️", "🏚️", "🏠", "🏡", "🏢", "🏣", "🏤", "🏥", "🏦", "🏨",
      "🏩", "🏪", "🏫", "🏬", "🏭", "🏯", "🏰", "💒", "🗼", "🗽",
      "⛪", "🕌", "🛕", "🕍", "⛩️", "🕋", "⛲", "⛺", "🌁", "🌃",
      "🏙️", "🌄", "🌅", "🌆", "🌇", "🌉", "♨️", "🎠", "🎡", "🎢",
      "💈", "🎪", "🚂", "🚃", "🚄", "🚅", "🚆", "🚇", "🚈", "🚉",
      "🚊", "🚝", "🚞", "🚋", "🚌", "🚍", "🚎", "🚐", "🚑", "🚒",
      "🚓", "🚔", "🚕", "🚖", "🚗", "🚘", "🚙", "🚚", "🚛", "🚜",
      "🏎️", "🏍️", "🛵", "🦽", "🦼", "🛺", "🚲", "🛴", "🛹", "🚏",
      "🛣️", "🛤️", "🛢️", "⛽", "🚨", "🚥", "🚦", "🛑", "🚧", "⚓",
      "⛵", "🛶", "🚤", "🛳️", "⛴️", "🛥️", "🚢", "✈️", "🛩️", "🛫",
      "🛬", "🪂", "💺", "🚁", "🚟", "🚠", "🚡", "🛰️", "🚀", "🛸",
      "🛎️", "🧳", "⌛", "⏳", "⌚", "⏰", "⏱️", "⏲️", "🕰️", "🕛",
      "🕧", "🕐", "🕜", "🕑", "🕝", "🕒", "🕞", "🕓", "🕟", "🕔",
      "🕠", "🕕", "🕡", "🕖", "🕢", "🕗", "🕣", "🕘", "🕤", "🕙",
      "🕥", "🕚", "🕦", "🌑", "🌒", "🌓", "🌔", "🌕", "🌖", "🌗",
      "🌘", "🌙", "🌚", "🌛", "🌜", "🌡️", "☀️", "🌝", "🌞", "🪐",
      "⭐", "🌟", "🌠", "🌌", "☁️", "⛅", "⛈️", "🌤️", "🌥️", "🌦️",
      "🌧️", "🌨️", "🌩️", "🌪️", "🌫️", "🌬️", "🌀", "🌈", "🌂", "☂️",
      "☔", "⛱️", "⚡", "❄️", "☃️", "⛄", "☄️", "🔥", "💧", "🌊",
    ],
  },
  {
    id: "activities",
    icon: "⚽",
    labelKey: "messages_emoji_group_activities",
    emojis: [
      "🎃", "🎄", "🎆", "🎇", "🧨", "✨", "🎈", "🎉", "🎊", "🎋",
      "🎍", "🎎", "🎏", "🎐", "🎑", "🧧", "🎀", "🎁", "🎗️", "🎟️",
      "🎫", "🎖️", "🏆", "🏅", "🥇", "🥈", "🥉", "⚽", "⚾", "🥎",
      "🏀", "🏐", "🏈", "🏉", "🎾", "🥏", "🎳", "🏏", "🏑", "🏒",
      "🥍", "🏓", "🏸", "🥊", "🥋", "🥅", "⛳", "⛸️", "🎣", "🤿",
      "🎽", "🎿", "🛷", "🥌", "🎯", "🪀", "🪁", "🔫", "🎱", "🔮",
      "🎮", "🕹️", "🎰", "🎲", "🧩", "🧸", "♠️", "♥️", "♦️", "♣️",
      "♟️", "🃏", "🀄", "🎴", "🎭", "🖼️", "🎨", "🧵", "🧶",
    ],
  },
  {
    id: "objects",
    icon: "💡",
    labelKey: "messages_emoji_group_objects",
    emojis: [
      "👓", "🕶️", "🥽", "🥼", "🦺", "👔", "👕", "👖", "🧣", "🧤",
      "🧥", "🧦", "👗", "👘", "🥻", "🩱", "🩲", "🩳", "👙", "👚",
      "👛", "👜", "👝", "🛍️", "🎒", "👞", "👟", "🥾", "🥿", "👠",
      "👡", "🩰", "👢", "👑", "👒", "🎩", "🎓", "🧢", "⛑️", "📿",
      "💄", "💍", "💎", "🔇", "🔈", "🔉", "🔊", "📢", "📣", "📯",
      "🔔", "🔕", "🎼", "🎵", "🎶", "🎙️", "🎚️", "🎛️", "🎤", "🎧",
      "📻", "🎷", "🎺", "🎸", "🎹", "🎻", "🪕", "🥁", "📱", "📲",
      "☎️", "📞", "📟", "📠", "🔋", "🔌", "💻", "🖥️", "🖨️", "⌨️",
      "🖱️", "🖲️", "💽", "💾", "💿", "📀", "🧮", "🎥", "🎞️", "📽️",
      "🎬", "📺", "📷", "📸", "📹", "📼", "🔍", "🔎", "🕯️", "💡",
      "🔦", "🏮", "🪔", "📔", "📕", "📖", "📗", "📘", "📙", "📚",
      "📓", "📒", "📃", "📜", "📄", "📰", "🗞️", "📑", "🔖", "🏷️",
      "💰", "💴", "💵", "💶", "💷", "💸", "💳", "🧾", "💹", "✉️",
      "📧", "📨", "📩", "📤", "📥", "📦", "📫", "📪", "📬", "📭",
      "📮", "🗳️", "✏️", "✒️", "🖋️", "🖊️", "🖌️", "🖍️", "📝", "💼",
      "📁", "📂", "🗂️", "📅", "📆", "🗒️", "🗓️", "📇", "📈", "📉",
      "📊", "📋", "📌", "📍", "📎", "🖇️", "📏", "📐", "✂️", "🗃️",
      "🗄️", "🗑️", "🔒", "🔓", "🔏", "🔐", "🔑", "🗝️", "🔨", "🪓",
      "⛏️", "⚒️", "🛠️", "🗡️", "⚔️", "💣", "🏹", "🛡️", "🔧", "🔩",
      "⚙️", "🗜️", "⚖️", "🦯", "🔗", "⛓️", "🧰", "🧲", "⚗️", "🧪",
      "🧫", "🧬", "🔬", "🔭", "📡", "💉", "🩸", "💊", "🩹", "🩺",
      "🚪", "🛏️", "🛋️", "🪑", "🚽", "🚿", "🛁", "🪒", "🧴", "🧷",
      "🧹", "🧺", "🧻", "🧼", "🧽", "🧯", "🛒", "🚬", "⚰️", "⚱️",
      "🧿", "🗿",
    ],
  },
  {
    id: "symbols",
    icon: "🔣",
    labelKey: "messages_emoji_group_symbols",
    emojis: [
      "🏧", "🚮", "🚰", "♿", "🚹", "🚺", "🚻", "🚼", "🚾", "🛂",
      "🛃", "🛄", "🛅", "⚠️", "🚸", "⛔", "🚫", "🚳", "🚭", "🚯",
      "🚱", "🚷", "📵", "🔞", "☢️", "☣️", "⬆️", "↗️", "➡️", "↘️",
      "⬇️", "↙️", "⬅️", "↖️", "↕️", "↔️", "↩️", "↪️", "⤴️", "⤵️",
      "🔃", "🔄", "🔙", "🔚", "🔛", "🔜", "🔝", "🛐", "⚛️", "🕉️",
      "✡️", "☸️", "☯️", "✝️", "☦️", "☪️", "☮️", "🕎", "🔯", "♈",
      "♉", "♊", "♋", "♌", "♍", "♎", "♏", "♐", "♑", "♒",
      "♓", "⛎", "🔀", "🔁", "🔂", "▶️", "⏩", "⏭️", "⏯️", "◀️",
      "⏪", "⏮️", "🔼", "⏫", "🔽", "⏬", "⏸️", "⏹️", "⏺️", "⏏️",
      "🎦", "🔅", "🔆", "📶", "📳", "📴", "♀️", "♂️", "✖️", "➕",
      "➖", "➗", "♾️", "‼️", "⁉️", "❓", "❔", "❕", "❗", "〰️",
      "💱", "💲", "⚕️", "♻️", "⚜️", "🔱", "📛", "🔰", "⭕", "✅",
      "☑️", "✔️", "❌", "❎", "➰", "➿", "〽️", "✳️", "✴️", "❇️",
      "©️", "®️", "™️", "#️⃣", "*️⃣", "0️⃣", "1️⃣", "2️⃣", "3️⃣", "4️⃣",
      "5️⃣", "6️⃣", "7️⃣", "8️⃣", "9️⃣", "🔟", "🔠", "🔡", "🔢", "🔣",
      "🔤", "🅰️", "🆎", "🅱️", "🆑", "🆒", "🆓", "ℹ️", "🆔", "Ⓜ️",
      "🆕", "🆖", "🅾️", "🆗", "🅿️", "🆘", "🆙", "🆚", "🈁", "🈂️",
      "🈷️", "🈶", "🈯", "🉐", "🈹", "🈚", "🈲", "🉑", "🈸", "🈴",
      "🈳", "㊗️", "㊙️", "🈺", "🈵", "🔴", "🟠", "🟡", "🟢", "🔵",
      "🟣", "🟤", "⚫", "⚪", "🟥", "🟧", "🟨", "🟩", "🟦", "🟪",
      "🟫", "⬛", "⬜", "◼️", "◻️", "◾", "◽", "▪️", "▫️", "🔶",
      "🔷", "🔸", "🔹", "🔺", "🔻", "💠", "🔘", "🔳", "🔲",
    ],
  },
  {
    id: "flags",
    icon: "🏁",
    labelKey: "messages_emoji_group_flags",
    emojis: [
      "🏁", "🚩", "🎌", "🏴", "🏳️", "🏳️‍🌈", "🏴‍☠️",
    ],
  },
];

/*
 * Text shortcuts that turn into an emoji while typing, e.g. ":D" -> 😄.
 *
 * A shortcut is only replaced when it is a whole whitespace-separated token and
 * the writer has just typed the space / new line after it (or sends the message
 * right after it). That keeps URLs ("https://..."), markup like "(b: ...)" and
 * words that merely contain these characters untouched.
 *
 * A token may also be several shortcuts written back to back (":D:D:D"), mixed
 * with emoji that are already there ("😄:D") — see convertShortcutToken. The
 * rule is still "the whole token or nothing": if any part of it is not a
 * shortcut or an emoji, the token stays exactly as typed.
 *
 * Lowercase variants are listed on purpose: sözlük convention writes everything
 * in lowercase, so ":d" and ":p" are at least as common as ":D" and ":P".
 */
export const EMOJI_SHORTCUTS: Record<string, string> = {
  ":)": "🙂",
  ":-)": "🙂",
  ":D": "😄",
  ":d": "😄",
  ":-D": "😄",
  "xD": "😆",
  "XD": "😆",
  "xd": "😆",
  ";)": "😉",
  ";-)": "😉",
  ":P": "😛",
  ":p": "😛",
  ":-P": "😛",
  ":(": "🙁",
  ":-(": "🙁",
  ":'(": "😢",
  ":O": "😮",
  ":o": "😮",
  ":*": "😘",
  ":|": "😐",
  ":/": "😕",
  "-_-": "😑",
  "^^": "😊",
  "B)": "😎",
  ":@": "😠",
  "<3": "❤️",
  "</3": "💔",
};

/*
 * Reverse lookup: emoji -> every shortcut that produces it, in the order they are
 * listed above (e.g. 😄 -> [":D", ":d", ":-D"]). Used by the emoji popup to show
 * an emoji's shortcuts while it is hovered. Built once at module load.
 */
export const EMOJI_SHORTCUTS_BY_EMOJI: Record<string, string[]> = Object.entries(
  EMOJI_SHORTCUTS
).reduce<Record<string, string[]>>((acc, [shortcut, emoji]) => {
  if (!acc[emoji]) acc[emoji] = [];
  acc[emoji].push(shortcut);
  return acc;
}, {});

// Shortcuts longest-first, so ":-D" is tried before any shorter shortcut at the same spot
const SHORTCUTS_LONGEST_FIRST = Object.keys(EMOJI_SHORTCUTS).sort((a, b) => b.length - a.length);

/*
 * One emoji already present in the token: a pictographic character, optionally
 * followed by a variation selector / skin tone, optionally joined with more by
 * ZWJ (e.g. "❤️", "👍🏽", "🏳️‍🌈"). Sticky flag so it only matches at lastIndex.
 *
 * Built with the RegExp constructor on purpose: tsconfig targets ES5, and
 * TypeScript 5.5+ rejects the "u" / "y" flags on regex LITERALS for that target.
 * Every browser in the production browserslist supports them at runtime.
 */
const EMOJI_AT_POSITION = new RegExp(
  "\\p{Extended_Pictographic}[\\uFE0F\\u{1F3FB}-\\u{1F3FF}]*" +
    "(?:\\u200D\\p{Extended_Pictographic}[\\uFE0F\\u{1F3FB}-\\u{1F3FF}]*)*",
  "uy"
);

/**
 * Convert a whitespace-free token that consists ENTIRELY of shortcuts and
 * existing emoji: ":D:D:D" -> "😄😄😄", "😄:D" -> "😄😄", ":)<3" -> "🙂❤️".
 *
 * Returns null when some part of the token is neither (":Dabc", "https://x"),
 * or when it contains no shortcut at all (a token of emoji only needs no change).
 *
 * Small backtracking search: at every position a shortcut (longest first) or an
 * emoji is tried, and the first split that consumes the whole token wins.
 * Tokens are a few characters long, so this is cheap.
 */
function convertShortcutToken(token: string): string | null {
  const convertFrom = (pos: number): { text: string; usedShortcut: boolean } | null => {
    if (pos === token.length) return { text: "", usedShortcut: false };

    for (const shortcut of SHORTCUTS_LONGEST_FIRST) {
      if (token.startsWith(shortcut, pos)) {
        const rest = convertFrom(pos + shortcut.length);
        if (rest) return { text: EMOJI_SHORTCUTS[shortcut] + rest.text, usedShortcut: true };
      }
    }

    EMOJI_AT_POSITION.lastIndex = pos;
    const emojiMatch = EMOJI_AT_POSITION.exec(token);
    if (emojiMatch) {
      const rest = convertFrom(pos + emojiMatch[0].length);
      if (rest) return { text: emojiMatch[0] + rest.text, usedShortcut: rest.usedShortcut };
    }

    return null;
  };

  const result = convertFrom(0);
  return result && result.usedShortcut ? result.text : null;
}

/**
 * Replace the shortcut token that ends at `end` (exclusive) with its emoji.
 *
 * Returns the new text and how much the text length changed, so the caller can
 * move the caret by the same amount; returns null when the token before `end`
 * is not a shortcut.
 *
 * The token may hold several shortcuts in a row (":D:D:D") — they are all
 * converted together through convertShortcutToken.
 */
export function replaceEmojiShortcutAt(
  text: string,
  end: number
): { text: string; delta: number } | null {
  // Token start = the character after the previous whitespace (or the text start)
  let start = end;
  while (start > 0 && !/\s/.test(text[start - 1])) {
    start--;
  }
  const token = text.slice(start, end);
  const converted = convertShortcutToken(token);
  if (!converted) return null;

  return {
    text: text.slice(0, start) + converted + text.slice(end),
    delta: converted.length - token.length,
  };
}
